import { useState, useEffect } from 'react';
import { visitsService } from '../../services/api';
import './WebsiteVisits.css';

const RANGES = [7, 30];

function dayLetter(day, days) {
  const d = new Date(day + 'T12:00:00');
  return days <= 7
    ? d.toLocaleDateString('en-AU', { weekday: 'short' })
    : d.toLocaleDateString('en-AU', { day: 'numeric' });
}

function ago(utc) {
  const mins = Math.round((Date.now() - new Date(utc.replace(' ', 'T') + 'Z').getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

function Change({ now, before }) {
  if (!before && !now) return null;
  if (!before) return <span className="wv-change up">new</span>;
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return <span className="wv-change">same</span>;
  return <span className={`wv-change ${pct > 0 ? 'up' : 'down'}`}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%</span>;
}

// Website visits (GID-Session-022): who reached the landing site and /book, and from where.
export default function WebsiteVisits() {
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    visitsService.summary(days).then((r) => {
      if (r.success) { setSummary(r.summary); setError(null); } else setError(r.error);
    });
  }, [days]);

  if (error) return <div className="dashboard-section wv"><p className="wv-muted">Website visits: {error}</p></div>;
  if (!summary) return null;

  const { totals, previousTotals, bySource, byPage, daily, lastVisit, countingSince, internalViews } = summary;
  const maxDay = Math.max(1, ...daily.map((d) => d.visitors));
  const maxSource = Math.max(1, ...bySource.map((s) => s.visitors));

  return (
    <div className="dashboard-section wv">
      <div className="section-header">
        <h2>Website visits</h2>
        <div className="wv-range" role="group" aria-label="Period">
          {RANGES.map((n) => (
            <button key={n} className={n === days ? 'active' : ''} onClick={() => setDays(n)}>{n} days</button>
          ))}
        </div>
      </div>

      <div className="wv-figures">
        <div><span className="wv-value">{totals.visitors}</span><span className="wv-label">Visitors <Change now={totals.visitors} before={previousTotals.visitors} /></span></div>
        <div><span className="wv-value">{totals.views}</span><span className="wv-label">Page views</span></div>
        <div><span className="wv-value">{totals.quotes}</span><span className="wv-label">Quote requests</span></div>
        <div><span className="wv-value">{totals.bookings}</span><span className="wv-label">Booked online</span></div>
      </div>

      <div className={`wv-days ${days > 7 ? 'many' : ''}`} aria-label="Visitors per day">
        {daily.map((d) => (
          <div key={d.day} className="wv-day" title={`${d.day}: ${d.visitors} visitor${d.visitors === 1 ? '' : 's'}, ${d.views} view${d.views === 1 ? '' : 's'}${d.bookings ? `, ${d.bookings} booked` : ''}`}>
            <div className="wv-bar-wrap"><div className="wv-bar" style={{ height: `${(d.visitors / maxDay) * 100}%` }} /></div>
            <span>{dayLetter(d.day, days)}</span>
          </div>
        ))}
      </div>

      {bySource.length === 0 ? (
        <p className="wv-muted wv-empty">No visitors in the last {days} days.{countingSince ? '' : ' Counting starts with the first visit.'}</p>
      ) : (
        <>
          <h3 className="wv-sub">Where they came from</h3>
          <ul className="wv-sources">
            {bySource.map((s) => (
              <li key={s.source}>
                <span className="wv-source-name">{s.label}</span>
                <span className="wv-source-bar"><span style={{ width: `${(s.visitors / maxSource) * 100}%` }} /></span>
                <span className="wv-source-count">{s.visitors}{s.quotes ? <em> · {s.quotes} quote{s.quotes === 1 ? '' : 's'}</em> : null}{s.bookings ? <em> · {s.bookings} booked</em> : null}</span>
              </li>
            ))}
          </ul>
          <p className="wv-pages">{byPage.map((p) => `${p.label} ${p.views}`).join(' · ')}</p>
        </>
      )}

      <p className="wv-muted wv-foot">
        {lastVisit ? `Last visitor ${ago(lastVisit.at)} (${lastVisit.label}). ` : ''}
        {countingSince ? `Counting since ${new Date(countingSince + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}. ` : ''}
        {internalViews ? `Your own visits while signed in aren't counted (${internalViews}).` : ''}
      </p>
    </div>
  );
}
